import { useMemo, useState } from "react";
import { MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "./constants";

const gbp = (n) =>
  "£" + Math.round(n).toLocaleString("en-GB");

const SliderRow = ({ testid, label, value, display, min, max, step, onChange }) => {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm text-slate-400">{label}</label>
        <span className="font-mono text-sm font-semibold text-amber-400">
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="kw-range"
        style={{ "--fill": `${fill}%` }}
        data-testid={testid}
        aria-label={label}
      />
    </div>
  );
};

export default function Calculator() {
  const [propertyValue, setPropertyValue] = useState(350000);
  const [deposit, setDeposit] = useState(52500);
  const [years, setYears] = useState(25);
  const [rate, setRate] = useState(4.5);

  const safeDeposit = Math.min(deposit, propertyValue * 0.9);

  const { loan, monthly, totalInterest, ltv } = useMemo(() => {
    const loanAmt = Math.max(propertyValue - safeDeposit, 0);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m =
      r > 0 ? (loanAmt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : loanAmt / n;
    return {
      loan: loanAmt,
      monthly: m,
      totalInterest: m * n - loanAmt,
      ltv: propertyValue > 0 ? (loanAmt / propertyValue) * 100 : 0,
    };
  }, [propertyValue, safeDeposit, years, rate]);

  const principalShare = loan > 0 ? loan / (loan + totalInterest) : 1;
  const R = 64;
  const C = 2 * Math.PI * R;

  const ctaText = `Hi Kieran, I had a play with your calculator — property ${gbp(propertyValue)}, deposit ${gbp(safeDeposit)}, ${years} years at ${rate}%. It says roughly ${gbp(monthly)} a month. Can we chat through it?`;

  return (
    <section id="calculator" data-testid="calculator-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl mb-14">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-amber-400/90 mb-5">
            The Calculator
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight">
            See what it'd cost each month.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Play with the sliders and watch the numbers move. Like what you
            see? Send the figures straight to me on WhatsApp with one tap.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-3xl border border-white/10 bg-[#073B4C] p-8 lg:p-10 space-y-9">
              <SliderRow
                testid="calculator-property-slider"
                label="Property value"
                value={propertyValue}
                display={gbp(propertyValue)}
                min={100000}
                max={2000000}
                step={5000}
                onChange={setPropertyValue}
              />
              <SliderRow
                testid="calculator-deposit-slider"
                label="Deposit"
                value={safeDeposit}
                display={gbp(safeDeposit)}
                min={10000}
                max={500000}
                step={2500}
                onChange={setDeposit}
              />
              <SliderRow
                testid="calculator-term-slider"
                label="Mortgage term"
                value={years}
                display={`${years} years`}
                min={5}
                max={35}
                step={1}
                onChange={setYears}
              />
              <SliderRow
                testid="calculator-rate-slider"
                label="Interest rate"
                value={rate}
                display={`${rate.toFixed(2)}%`}
                min={1}
                max={10}
                step={0.05}
                onChange={setRate}
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-amber-400/25 bg-gradient-to-b from-[#0A3D50] to-[#052B38] p-8 lg:p-10 flex flex-col">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-400">
                  Rough monthly payment
                </p>
                <span
                  data-testid="calculator-ltv-badge"
                  className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 font-mono text-[11px] tracking-wider text-amber-300"
                >
                  LTV {ltv.toFixed(1)}%
                </span>
              </div>
              <p
                data-testid="calculator-monthly-payment"
                className="mt-4 font-mono text-5xl sm:text-6xl font-semibold text-white tracking-tight"
              >
                {gbp(monthly)}
                <span className="text-lg text-slate-500 font-normal">/mo</span>
              </p>

              <div className="mt-9 flex items-center gap-8">
                <svg width="150" height="150" viewBox="0 0 150 150" className="shrink-0 -rotate-90">
                  <circle cx="75" cy="75" r={R} fill="none" stroke="#0B4256" strokeWidth="14" />
                  <circle
                    cx="75"
                    cy="75"
                    r={R}
                    fill="none"
                    stroke="#E5BFB6"
                    strokeWidth="14"
                    strokeLinecap="round"
                    strokeDasharray={`${C * principalShare} ${C}`}
                    data-testid="calculator-principal-arc"
                  />
                </svg>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="flex items-center gap-2 text-slate-400">
                      <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                      You'd borrow
                    </p>
                    <p className="mt-1 font-mono font-semibold text-white" data-testid="calculator-loan-amount">
                      {gbp(loan)}
                    </p>
                  </div>
                </div>
              </div>

              <a
                href={waLink(ctaText)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calculator-whatsapp-cta"
                className="mt-auto pt-9"
              >
                <span className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-[#03222D] transition-all duration-300 hover:bg-[#2be072]">
                  <MessageCircle size={17} />
                  Chat Through These Figures with Me
                </span>
              </a>
              <p className="mt-4 text-[11px] leading-relaxed text-slate-500">
                Just a rough guide — your real rate depends on the lender and
                your circumstances. I'll find you the exact figures.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
